/**
 * Conversational planning panel.
 *
 * Three things the UI must get right, all from PRD section 9:
 *   - tool calls are visible, so the planner sees what the assistant did
 *     rather than trusting a paragraph;
 *   - a mutating action surfaces an explicit confirm/cancel, never an
 *     auto-accept;
 *   - a guardrail event is shown as itself, not swallowed.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { RequestFailed, api, streamAgentTurn } from '../lib/api'
import type { AgentEvent } from '../lib/types'
import { parseMessageBlocks, titleCase } from '../lib/format'
import { Callout } from './Callout'

interface Entry {
  id: string
  kind: 'user' | 'assistant' | 'tool' | 'guardrail' | 'error' | 'note'
  text: string
  detail?: string
}

interface PendingConfirmation {
  action: string
  message: string
  arguments: Record<string, unknown>
}

const TOOL_LABEL: Record<string, string> = {
  parse_campaign_brief: 'Reading the brief',
  update_campaign_brief: 'Updating the brief',
  validate_campaign_brief: 'Validating the brief',
  search_assets: 'Searching Captura inventory',
  get_asset_intelligence: 'Retrieving asset intelligence',
  run_recommendation: 'Scoring and optimizing',
  check_availability_and_price: 'Checking availability and price',
  save_draft_plan: 'Saving a plan version',
  compare_plans: 'Comparing plan versions',
  request_approval: 'Submitting for approval',
  create_campaign: 'Creating the campaign',
  get_session_state: 'Reading session state',
}

/** A chat bubble's text, with any markdown table rendered as an actual table. */
function MessageContent({ text }: { text: string }) {
  const blocks = parseMessageBlocks(text)
  return (
    <>
      {blocks.map((block, i) =>
        block.type === 'table' ? (
          <div className="table-wrap" key={i}>
            <table>
              <thead>
                <tr>
                  {block.header.map((cell, c) => (
                    <th key={c}>{cell}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => (
                      <td key={c}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div key={i}>{block.text}</div>
        ),
      )}
    </>
  )
}

export function AgentPanel({
  sessionId,
  llmAvailable,
  llmModel,
  onStateChange,
  onRunCreated,
  onPlanChanged,
  prefill,
  autoKickoff,
}: {
  sessionId: string | null
  llmAvailable: boolean
  llmModel: string | null
  onStateChange?: (data: Record<string, any>) => void
  onRunCreated?: (runId: string) => void
  onPlanChanged?: (planId: string) => void
  //: Fills the message box with `text` (not sent) whenever `token` changes -
  //: e.g. a Brief-panel clarification suggestion. `token` exists because a
  //: plain string prop wouldn't re-fire the effect on a repeat click of the
  //: same suggestion.
  prefill?: { text: string; token: number } | null
  //: The highest-priority outstanding clarification (blocking first), if
  //: any. When the transcript for this session turns out to be empty, it is
  //: shown as though the assistant had asked it unprompted - so the planner
  //: never has to speak first to get the Q&A going. No LLM call is spent on
  //: this: the question text already comes from the same deterministic
  //: validation that drives the Brief panel's clarification cards.
  autoKickoff?: { question: string; why?: string | null } | null
}) {
  const [entries, setEntries] = useState<Entry[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [pending, setPending] = useState<PendingConfirmation | null>(null)
  const [tokens, setTokens] = useState<number | null>(null)
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const chatRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const nextId = useRef(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const kickoffFiredFor = useRef<string | null>(null)

  useEffect(() => {
    if (!prefill) return
    setInput(prefill.text)
    textareaRef.current?.focus()
  }, [prefill?.token]) // eslint-disable-line react-hooks/exhaustive-deps

  const push = useCallback((entry: Omit<Entry, 'id'>) => {
    setEntries((prev) => [...prev, { ...entry, id: `e${nextId.current++}` }])
  }, [])

  // Load the transcript when the session changes, so a reload keeps context.
  useEffect(() => {
    if (!sessionId) return
    let cancelled = false
    setHistoryLoaded(false)
    api
      .messages(sessionId)
      .then((rows) => {
        if (cancelled) return
        setEntries(
          rows
            .map<Entry>((row, index) => ({
              id: `h${index}`,
              kind: row.role === 'user' ? 'user' : 'assistant',
              text: row.content ?? '',
            }))
            .filter((entry) => entry.text.trim().length > 0),
        )
      })
      .catch(() => {
        /* a fresh session has no transcript yet */
      })
      .finally(() => {
        if (!cancelled) setHistoryLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [sessionId])

  // Proactively open with the top outstanding clarification, once per
  // session, so the planner lands on a Q&A already in progress rather than
  // a blank box waiting for them to speak first.
  useEffect(() => {
    if (!sessionId || !historyLoaded || !autoKickoff || entries.length > 0) return
    if (kickoffFiredFor.current === sessionId) return
    kickoffFiredFor.current = sessionId
    push({
      kind: 'assistant',
      text: autoKickoff.why ? `${autoKickoff.question}\n\n${autoKickoff.why}` : autoKickoff.question,
    })
  }, [sessionId, historyLoaded, autoKickoff, entries.length, push])

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' })
  }, [entries, pending])

  const handleEvent = useCallback(
    (event: AgentEvent) => {
      switch (event.type) {
        case 'message':
          if (event.data.text) {
            push({ kind: 'assistant', text: String(event.data.text) })
          }
          break
        case 'tool_call':
          push({
            kind: 'tool',
            text: TOOL_LABEL[event.data.name] ?? titleCase(String(event.data.name)),
            detail: event.data.mutating ? 'changes state' : undefined,
          })
          break
        case 'tool_result': {
          const result = event.data.result ?? {}
          if (event.data.error) {
            push({
              kind: 'guardrail',
              text: String(event.data.error.message ?? 'The tool call was refused.'),
              detail: String(event.data.error.code ?? ''),
            })
          }
          if (result.run_id && onRunCreated) onRunCreated(String(result.run_id))
          if (result.plan_id && onPlanChanged) onPlanChanged(String(result.plan_id))
          break
        }
        case 'guardrail':
          if (event.data.type === 'confirmation_required') {
            setPending({
              action: String(event.data.action),
              message: String(event.data.message ?? 'Confirm this action?'),
              arguments: event.data.arguments ?? {},
            })
          } else {
            push({
              kind: 'guardrail',
              text: String(event.data.message ?? titleCase(String(event.data.type))),
            })
          }
          break
        case 'state':
          onStateChange?.(event.data)
          break
        case 'error':
          push({ kind: 'error', text: String(event.data.message ?? 'The assistant failed.') })
          break
        case 'done':
          if (typeof event.data.session_tokens_used === 'number') {
            setTokens(event.data.session_tokens_used)
          }
          break
      }
    },
    [push, onRunCreated, onPlanChanged, onStateChange],
  )

  const send = useCallback(
    async (message: string, confirm = false) => {
      if (!sessionId || !message.trim() || busy) return
      push({ kind: 'user', text: message })
      setInput('')
      setPending(null)
      setBusy(true)
      abortRef.current = new AbortController()
      try {
        await streamAgentTurn(sessionId, message, {
          confirm,
          onEvent: handleEvent,
          signal: abortRef.current.signal,
        })
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          push({ kind: 'note', text: 'Stopped.' })
        } else {
          const message =
            error instanceof RequestFailed
              ? error.error.message
              : 'The assistant could not be reached.'
          push({ kind: 'error', text: message })
        }
      } finally {
        setBusy(false)
        abortRef.current = null
      }
    },
    [sessionId, busy, push, handleEvent],
  )

  if (!sessionId) {
    return (
      <div className="card-body">
        <Callout tone="info" title="No planning session">
          Start a brief to open a conversation with the planning assistant.
        </Callout>
      </div>
    )
  }

  return (
    <>
      <div className="card-head">
        <div className="grow">
          <h2>Planning assistant</h2>
          <div className="tiny faint">
            {llmAvailable
              ? `${llmModel} · interprets and explains; it never calculates`
              : 'No model configured · deterministic extraction only'}
            {tokens !== null ? ` · ${tokens.toLocaleString()} tokens used` : ''}
          </div>
        </div>
        {busy ? (
          <button className="small ghost" onClick={() => abortRef.current?.abort()}>
            Stop
          </button>
        ) : null}
      </div>

      <div className="chat" ref={chatRef} aria-live="polite">
        {entries.length === 0 ? (
          <div className="bubble system">
            Describe the campaign in your own words. For example: “Launch campaign for a
            quick-commerce brand in Mumbai, 18 to 30 year olds, mid to premium, ₹10 lakh, one month
            from 1 November, prefer sites near IT parks and colleges.”
          </div>
        ) : null}

        {entries.map((entry) => {
          if (entry.kind === 'tool') {
            return (
              <div key={entry.id} className="tool-line">
                <span aria-hidden>→</span>
                {entry.text}
                {entry.detail ? <span className="chip warn">{entry.detail}</span> : null}
              </div>
            )
          }
          if (entry.kind === 'guardrail') {
            return (
              <div key={entry.id} className="bubble system">
                <strong>Guardrail:</strong> {entry.text}
                {entry.detail ? <span className="mono faint"> ({entry.detail})</span> : null}
              </div>
            )
          }
          if (entry.kind === 'error') {
            return (
              <div key={entry.id} className="bubble system" style={{ color: 'var(--danger)' }}>
                {entry.text}
              </div>
            )
          }
          if (entry.kind === 'note') {
            return (
              <div key={entry.id} className="bubble system">
                {entry.text}
              </div>
            )
          }
          return (
            <div key={entry.id} className={`bubble ${entry.kind}`}>
              <MessageContent text={entry.text} />
            </div>
          )
        })}

        {busy ? (
          <div className="tool-line">
            <span aria-hidden>·</span> Working…
          </div>
        ) : null}
      </div>

      <div className="composer">
        {pending ? (
          <Callout tone="warn" title={pending.message}>
            <div className="tiny mono faint" style={{ marginBottom: 6 }}>
              {pending.action}
              {Object.keys(pending.arguments).length
                ? ` · ${JSON.stringify(pending.arguments).slice(0, 160)}`
                : ''}
            </div>
            <div className="row tight">
              <button
                className="primary small"
                onClick={() => send('Yes, go ahead.', true)}
                disabled={busy}
              >
                Confirm
              </button>
              <button className="small" onClick={() => setPending(null)} disabled={busy}>
                Cancel
              </button>
            </div>
          </Callout>
        ) : null}

        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              void send(input)
            }
          }}
          placeholder={
            llmAvailable
              ? 'Describe or refine the campaign…  (⌘↵ to send)'
              : 'Describe the campaign; it will be parsed deterministically…  (⌘↵ to send)'
          }
          disabled={busy}
          aria-label="Message to the planning assistant"
        />
        <div className="pill-row composer-suggestions">
          {['More premium sites, hold the budget', 'Add sites near colleges', 'Improve reach'].map(
            (suggestion) => (
              <button
                key={suggestion}
                className="small"
                disabled={busy}
                onClick={() => setInput(suggestion)}
              >
                {suggestion}
              </button>
            ),
          )}
        </div>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="primary" onClick={() => void send(input)} disabled={busy || !input.trim()}>
            Send
          </button>
        </div>
      </div>
    </>
  )
}
